import { api } from './client';

export type TipoDoc = 'nfe' | 'cte' | 'cte-os' | 'mdfe';

export interface Rascunho {
  id: number;
  emitente_id?: number;
  tipo_documento?: TipoDoc;
  numero_proposto?: number | null;
  serie?: number | null;
  status?: string;
  motivo?: string | null;
  documento_gerado_id?: number | null;
  payload?: RascunhoNFePayload | Record<string, unknown>;
  // Campos legados usados pelo editor simplificado antigo
  descricao?: string;
  destinatario?: string;
  valor?: number | string;
  payload_json?: unknown;
  criado_em?: string;
  atualizado_em?: string;
  [k: string]: unknown;
}

export interface RascunhoNFeItem {
  produto_id: number;
  quantidade: number;
  valor_unitario: number;
  desconto?: number;
  cfop?: string;
  // Overrides — pré-preenchidos do produto mas editáveis por item na UI
  codigo?: string;         // cProd
  descricao?: string;      // xProd
  ncm?: string;
  cest?: string;           // CEST (NT 2015.003) — obrigatório para ST
  ucom?: string;           // uCom
  ean?: string;            // cEAN/cEANTrib
  utrib?: string;          // uTrib
  qtrib?: number;          // qTrib
  vuntrib?: number;        // vUnTrib
  orig?: string;           // origem ICMS
  csosn?: string;
  ipi_cst?: string;
  ipi_vbc?: number;
  ipi_pipi?: number;
  ipi_vipi?: number;
  pis_cst?: string;
  pis_vbc?: number;
  pis_ppis?: number;
  pis_vpis?: number;
  cofins_cst?: string;
  cofins_vbc?: number;
  cofins_pcofins?: number;
  cofins_vcofins?: number;
  inf_ad_prod?: string;    // observação do item (NF-e infAdProd, máx 500)
}

export interface RascunhoNFeFormaPagamento {
  tipo_pag: string; // 90=sem pagamento (default), 01=dinheiro, 03=cartao credito, 04=cartao debito, 15=boleto, 17=PIX
  valor_pag: number;
}

export interface RascunhoNFeTotais {
  frete?: number;
  seguro?: number;
  desconto?: number;
  outro?: number;
}

export interface RascunhoNFeTransporte {
  modFrete?: number; // 0=Emit, 1=Dest, 2=Terceiros, 3=Próprio rem, 4=Próprio dest, 9=Sem frete
  transporta?: {
    documento?: string;
    nome?: string;
    ie?: string;
    endereco?: string;
    municipio?: string;
    uf?: string;
  };
  volumes?: Array<{
    qVol?: number;
    esp?: string;
    marca?: string;
    nVol?: string;
    pesoL?: number;
    pesoB?: number;
  }>;
}

export interface RascunhoNFeCobranca {
  fatura?: {
    numero?: string;
    valor_original?: number;
    valor_desconto?: number;
    valor_liquido?: number;
  };
  duplicatas?: Array<{
    numero?: string;
    vencimento?: string;
    valor?: number;
  }>;
}

export interface RascunhoNFeDifal {
  ativar?: boolean;
  vBCUFDest?: number;
  pICMSUFDest?: number;
  pICMSInter?: number;
  pICMSInterPart?: number;
  vICMSUFDest?: number;
  vICMSUFRemet?: number;
}

export interface RascunhoNFePayload {
  natureza_operacao: string;
  destinatario_id: number;
  itens: RascunhoNFeItem[];
  finalidade?: number; // 1 Normal, 2 Complementar, 3 Ajuste, 4 Devolução
  forma_pagamento?: RascunhoNFeFormaPagamento;
  informacoes_adicionais?: string;
  tipo_nf?: number; // 0 entrada, 1 saida
  ind_final?: number; // 0 normal, 1 consumidor final
  ind_pres?: number; // 0..9 (presença comprador)
  ind_intermed?: number; // 0 sem intermediador, 1 marketplace/intermediador
  intermed_cnpj?: string;
  chave_referencia_nfe?: string;
  autxml_cpf?: string;
  autxml_cnpj?: string;
  dh_emi?: string;   // ISO local "YYYY-MM-DDTHH:MM" — opcional
  dh_sai?: string;   // idem para data de saída/entrada
  totais?: RascunhoNFeTotais;
  transporte?: RascunhoNFeTransporte;
  cobranca?: RascunhoNFeCobranca;
  difal?: RascunhoNFeDifal;
}

export interface RascunhoCTePayload {
  tomador_id?: number | null;
  remetente_id?: number | null;
  destinatario_id?: number | null;
  expedidor_id?: number | null;
  recebedor_id?: number | null;
  chave_nfe_vinculada?: string;
  valor_prestacao?: number;
  valor_total_nota?: number;
  uf_origem?: string;
  uf_destino?: string;
  cfop?: string;
  natureza_operacao?: string;
  produto_predominante?: string;
  peso_carga?: number;
  rntrc?: string;
  ciot?: string;
  placa?: string;
  renavam?: string;
  percurso_ufs?: string[];
  desc_servico?: string;
  qtd_pessoas?: number;
  data_viagem?: string;
  hora_viagem?: string;
  tipo_fretamento?: string;
  taf?: string;
  nro_reg_estadual?: string;
  duplicado_de_cte_id?: number | null;
}

/** Resultado do envio automático de e-mail pós-autorização (best-effort no backend). */
export interface EmailAutomatico {
  enviado: boolean;
  para?: string[];
  anexos?: string[];
  motivo?: string;
}

export interface TransmissaoResultado {
  sucesso: boolean;
  nfe_id?: number;
  cte_id?: number;
  cStat?: string;
  xMotivo?: string;
  chave?: string | null;
  protocolo?: string | null;
  status?: string;
  ambiente?: string;
  pdf_path?: string | null;
  email_automatico?: EmailAutomatico | null;
  erro?: string;
  detail?: string;
}

export interface CTe {
  id: number;
  emitente_id: number;
  tomador_id?: number | null;
  remetente_id?: number | null;
  destinatario_id?: number | null;
  numero: number;
  serie: number;
  modelo?: string;
  chave?: string | null;
  protocolo?: string | null;
  cfop?: string | null;
  chave_nfe_vinculada?: string | null;
  valor_prestacao: number;
  valor_total_nota?: number | null;
  uf_origem?: string | null;
  uf_destino?: string | null;
  ambiente: string;
  status: string;
  c_stat_sefaz?: string | null;
  x_motivo_sefaz?: string | null;
  data_emissao?: string | null;
  data_autorizacao?: string | null;
  pdf_path?: string | null;
}

export interface NFe {
  id: number;
  emitente_id: number;
  destinatario_id?: number | null;
  numero: number;
  serie: number;
  modelo: string;
  chave?: string | null;
  protocolo?: string | null;
  valor_produtos: number;
  valor_frete?: number;
  valor_desconto?: number;
  valor_total: number;
  ambiente: string;
  status: string;
  c_stat_sefaz?: string | null;
  x_motivo_sefaz?: string | null;
  natureza_operacao?: string | null;
  data_emissao?: string | null;
  data_autorizacao?: string | null;
  pdf_path?: string | null;
}

export interface ListDocumentosParams {
  emitenteId?: number;
  status?: string;
  limit?: number;
  offset?: number;
}

function buildListParams(opts?: ListDocumentosParams): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (opts?.emitenteId) params.emitente_id = opts.emitenteId;
  if (opts?.status) params.status = opts.status;
  if (typeof opts?.limit === 'number') params.limit = opts.limit;
  if (typeof opts?.offset === 'number') params.offset = opts.offset;
  return params;
}

export async function listRascunhos(tipo: TipoDoc, emitenteId?: number): Promise<Rascunho[]> {
  const params: Record<string, string | number> = {};
  if (emitenteId) params.emitente_id = emitenteId;
  const { data } = await api.get<Rascunho[]>(`/${tipo}/rascunhos`, { params });
  return data;
}

export async function getRascunho(tipo: TipoDoc, id: number): Promise<Rascunho> {
  if (tipo === 'nfe') {
    try {
      const { data } = await api.get<Rascunho>(`/nfe/rascunhos/${id}`);
      return data;
    } catch (e: unknown) {
      const status = typeof e === 'object' && e !== null && 'response' in e
        ? (e as { response?: { status?: number } }).response?.status
        : undefined;
      if (status === 404) {
        return criarRascunhoCorrecaoNFe(id);
      }
      throw e;
    }
  }
  if (tipo === 'cte') {
    try {
      const { data } = await api.get<Rascunho>(`/cte/rascunhos/${id}`);
      return data;
    } catch (e: unknown) {
      const status = typeof e === 'object' && e !== null && 'response' in e
        ? (e as { response?: { status?: number } }).response?.status
        : undefined;
      if (status === 404) {
        return criarRascunhoReenvioCTe(id);
      }
      throw e;
    }
  }
  const all = await listRascunhos(tipo);
  const found = all.find((r) => r.id === id);
  if (!found) throw new Error('Rascunho nao encontrado');
  return found;
}

export async function createRascunho(tipo: TipoDoc, payload: Partial<Rascunho>): Promise<Rascunho> {
  const { data } = await api.post<Rascunho>(`/${tipo}/rascunhos`, payload);
  return data;
}

export async function updateRascunho(tipo: TipoDoc, id: number, payload: Partial<Rascunho>): Promise<Rascunho> {
  const { data } = await api.put<Rascunho>(`/${tipo}/rascunhos/${id}`, payload);
  return data;
}

export async function deleteRascunho(tipo: TipoDoc, id: number): Promise<void> {
  await api.delete(`/${tipo}/rascunhos/${id}`);
}

export async function criarRascunhoCorrecaoNFe(nfeId: number): Promise<Rascunho> {
  const { data } = await api.post<Rascunho>(`/nfe/${nfeId}/criar-rascunho-correcao`, {});
  return data;
}

export async function criarRascunhoReenvioCTe(cteId: number): Promise<Rascunho> {
  const { data } = await api.post<Rascunho>(`/cte/${cteId}/reenviar`, {});
  return data;
}

/**
 * Transmite um RASCUNHO (não uma NFe já criada).
 * Endpoint: POST /api/nfe/rascunhos/<rid>/transmitir
 * Backend cria o registro NFe, assina, envia SEFAZ homologação.
 */
export async function transmitirRascunho(tipo: TipoDoc, id: number): Promise<TransmissaoResultado> {
  if (tipo === 'nfe' || tipo === 'cte') {
    const { data } = await api.post<TransmissaoResultado>(`/${tipo}/rascunhos/${id}/transmitir`, {});
    return data;
  }
  // Outros tipos ainda stubs
  const { data } = await api.post<TransmissaoResultado>(`/${tipo}/${id}/transmitir`, {});
  return data;
}

/** Duplica um CT-e existente → cria novo rascunho com parcerias copiadas,
 * chave_nfe_vinculada limpa. Jean cola a nova chave depois. */
export async function duplicarCTe(cteId: number): Promise<Rascunho> {
  const { data } = await api.post<Rascunho>(`/cte/${cteId}/duplicar`, {});
  return data;
}

/** Duplica uma NF-e existente → novo rascunho com destinatário, itens,
 * natureza de operação copiados; número/chave/protocolo zerados. */
export async function duplicarNFe(nfeId: number): Promise<Rascunho> {
  const { data } = await api.post<Rascunho>(`/nfe/${nfeId}/duplicar`, {});
  return data;
}

/** Duplica um CT-e OS (modelo 67). */
export async function duplicarCteOS(id: number): Promise<Rascunho> {
  const { data } = await api.post<Rascunho>(`/cte-os/${id}/duplicar`, {});
  return data;
}

export async function listCTes(emitenteId?: number, opts?: Omit<ListDocumentosParams, 'emitenteId'>): Promise<CTe[]> {
  const params = buildListParams({ emitenteId, ...opts });
  const { data } = await api.get<CTe[]>(`/cte`, { params });
  return data;
}

export async function listNFes(emitenteId?: number, opts?: Omit<ListDocumentosParams, 'emitenteId'>): Promise<NFe[]> {
  const params = buildListParams({ emitenteId, ...opts });
  const { data } = await api.get<NFe[]>(`/nfe`, { params });
  return data;
}
export async function listCTeOS(emitenteId?: number, opts?: Omit<ListDocumentosParams, 'emitenteId'>): Promise<CTe[]> {
  const params = buildListParams({ emitenteId, ...opts });
  const { data } = await api.get<CTe[]>(`/cte-os`, { params });
  return data;
}

export async function listMDFe(emitenteId?: number, opts?: Omit<ListDocumentosParams, 'emitenteId'>): Promise<CTe[]> {
  const params = buildListParams({ emitenteId, ...opts });
  const { data } = await api.get<CTe[]>(`/mdfe`, { params });
  return data;
}


/** Transmite uma NFe já criada (retransmissão). */
export async function transmitirNFeById(nfeId: number): Promise<TransmissaoResultado> {
  const { data } = await api.post<TransmissaoResultado>(`/nfe/${nfeId}/transmitir`, {});
  return data;
}

export async function listDocumentos(tipo: TipoDoc, emitenteId?: number): Promise<Rascunho[]> {
  const params: Record<string, string | number> = {};
  if (emitenteId) params.emitente_id = emitenteId;
  const { data } = await api.get<Rascunho[]>(`/${tipo}`, { params });
  return data;
}

export interface ContadoresDocumentos {
  emitente_id: number | null;
  emitidos: { nfe: number; cte: number; cte_os: number; mdfe: number };
  rascunhos: { nfe: number; cte: number; cte_os: number; mdfe: number };
  total_emitidos: number;
  total_rascunhos: number;
}

export async function getContadoresDocumentos(emitenteId?: number | null): Promise<ContadoresDocumentos> {
  const params: Record<string, number> = {};
  if (emitenteId != null) params.emitente_id = emitenteId;
  const { data } = await api.get<ContadoresDocumentos>('/documentos/contadores', { params });
  return data;
}
